import { Injectable, signal } from '@angular/core';

/**
 * localStorage key for the "viewing company" selection.
 * Must stay in sync with `ctUserCompanyStorageKey` in
 * user-company-droplist.component.ts (not imported directly here to avoid a
 * circular dependency between the core fwcore module and feature components).
 */
const ctUserCompanyStorageKey = 'selectedCompanyId';

/** Singleton state for the active impersonation session. */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {

  /** Email of the user being impersonated; null when inactive. */
  private email: string | null = null;

  /** Display name of the user being impersonated. */
  public name: string | null = null;

  /** True when an impersonation session is active. */
  private _impersonating = signal<boolean>(false);

  /** Read-only signal consumers can react to. */
  readonly impersonating = this._impersonating.asReadonly();

  /**
   * The company id of the user being impersonated (captured from the
   * company/user picker at the moment impersonation begins). This is the
   * company deal/scenario requests must filter by while impersonating -
   * NOT the admin's own company.
   */
  private _companyId = signal<string | null>(null);

  /** Read-only signal consumers can react to. */
  readonly companyId = this._companyId.asReadonly();

  /** Returns the active impersonation email, or null if not impersonating. */
  get(): string | null { return this.email; }

  /** Begins impersonation; sets email, optional display name, and the impersonated user's company id. */
  set(email?: string | null, name?: string | null, companyId?: string | null): void {

    this.email = email ?? null;
    this.name = name ?? null;
    this._companyId.set(companyId ?? null);

    // The previously viewed/selected company belongs to a different user
    // context (the admin, or a prior impersonation target) and must not
    // leak into the newly impersonated user's session.
    localStorage.removeItem(ctUserCompanyStorageKey);

    this._impersonating.set(true);
  }

  /** Ends impersonation by clearing all state. */
  reset(): void {

    this.email = null;
    this.name = null;
    this._companyId.set(null);

    localStorage.removeItem(ctUserCompanyStorageKey);

    this._impersonating.set(false);
  }
}
